/**
 * Shared district types for API contracts between frontend and backend
 *
 * Districts are geographic groupings of locations with district managers.
 * Behind the scenes, districts use the tagging system:
 * - Each district has a 1:1 Tag in a special "Districts" TagGroup
 * - Locations are assigned to districts via ResourceTag
 * - District managers are members of the district's ApproverGroup
 */
import { z } from "zod";
import { nameSchema, uuidSchema, hexColorOptionalSchema, } from "./validation-common.js";
// ===== INPUT SCHEMAS =====
/**
 * Schema for creating a district
 */
export const CreateDistrictInputSchema = z.object({
    name: nameSchema,
    color: hexColorOptionalSchema,
});
/**
 * Schema for updating a district
 */
export const UpdateDistrictInputSchema = z.object({
    name: nameSchema.optional(),
    color: hexColorOptionalSchema,
});
/**
 * Schema for batch adding locations to a district
 */
export const BatchAddLocationsInputSchema = z.object({
    locationIds: z.array(uuidSchema).min(1, "At least one location is required"),
});
// ===== RESPONSE SCHEMAS =====
/**
 * District manager response schema
 */
export const DistrictManagerResponseSchema = z.object({
    employmentId: z.string().uuid(),
    userId: z.string().uuid(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
});
export const DistrictLocationManagerSchema = z.object({
    employmentId: z.string().uuid(),
    userId: z.string().uuid(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
});
/**
 * District location response schema
 */
export const DistrictLocationResponseSchema = z.object({
    id: z.string().uuid(),
    name: z.string().nullable(),
    addressLine1: z.string(),
    addressLine2: z.string().nullable(),
    city: z.string(),
    state: z.string(),
    zipCode: z.string(),
    isActive: z.boolean(),
    managers: z.array(DistrictLocationManagerSchema),
});
/**
 * Compact district response schema (for lists)
 */
export const DistrictResponseSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    color: z.string().nullable(),
    companyId: z.string().uuid(),
    locationCount: z.number(),
    managerCount: z.number(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/**
 * Full district response schema with locations and managers
 */
export const DistrictDetailResponseSchema = DistrictResponseSchema.extend({
    locations: z.array(DistrictLocationResponseSchema),
    managers: z.array(DistrictManagerResponseSchema),
    permissionSetId: z.string().uuid().nullable(),
});
/**
 * Unassigned locations response schema
 */
export const UnassignedLocationsResponseSchema = z.object({
    locations: z.array(DistrictLocationResponseSchema),
});
/**
 * Result for a single location in batch add operation
 */
export const BatchAddLocationResultSchema = z.object({
    locationId: z.string().uuid(),
    success: z.boolean(),
    error: z.string().optional(),
    existingDistrictId: z.string().uuid().optional(),
    existingDistrictName: z.string().optional(),
});
/**
 * Batch add locations response schema
 */
export const BatchAddLocationsResponseSchema = z.object({
    district: DistrictDetailResponseSchema,
    results: z.array(BatchAddLocationResultSchema),
});
// ===== PARAMETER SCHEMAS =====
export const DistrictIdParamSchema = z.object({
    districtId: uuidSchema,
});
export const DistrictLocationParamSchema = z.object({
    districtId: uuidSchema,
    locationId: uuidSchema,
});
export const DistrictManagerParamSchema = z.object({
    districtId: uuidSchema,
    employmentId: uuidSchema,
});
// ===== BULK WITH-LOCATIONS TYPES =====
/**
 * Lightweight district + location shape for dropdown (avoids N+1)
 */
export const DistrictWithLocationsSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    color: z.string().nullable(),
    locationIds: z.array(z.uuid()),
});
export const DistrictsWithLocationsResponseSchema = z.object({
    districts: z.array(DistrictWithLocationsSchema),
    unassignedLocationIds: z.array(z.uuid()),
});
